import React from "react";
import {
  Mail,
  MessageSquareText,
  CheckSquare,
  CalendarDays,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Zap,
  Hexagon,
  Play,
  Users,
  FolderKanban,
} from "lucide-react";
import HeroDemo from "./HeroDemo";
import HexaBuddy from "./HexaBuddy";
import BetaChip from "./BetaChip";
import { useAuth } from "./AuthContext";

const FEATURES = [
  {
    id: "email",
    title: "AI-Based Email",
    blurb:
      "Inbox that reads itself. HEXA summarizes threads, drafts replies, extracts tasks, and schedules meetings — all before you've finished your coffee.",
    icon: Mail,
    tint: "#3b82f6",
    accent: "rgba(59,130,246,0.18)",
  },
  {
    id: "chat",
    title: "AI Chat",
    blurb:
      "One assistant across every app. Ask questions, run multi-step workflows, control the OS by voice — your second brain, available 24/7.",
    icon: MessageSquareText,
    tint: "#a78bfa",
    accent: "rgba(167,139,250,0.18)",
  },
  {
    id: "task",
    title: "AI Tasks",
    blurb:
      "Project boards that think with you. Auto-prioritize, auto-assign, auto-summarize — from a meeting note to a delivered task in one click.",
    icon: CheckSquare,
    tint: "#34d399",
    accent: "rgba(52,211,153,0.18)",
  },
  {
    id: "events",
    title: "AI Events & Meetings",
    blurb:
      "Smart calendar + native video meetings. HEXA prepares the agenda, takes notes, follows up, and turns conversations into next-step actions.",
    icon: CalendarDays,
    tint: "#0ea5e9",
    accent: "rgba(14,165,233,0.18)",
  },
  {
    id: "crm",
    title: "CRM",
    blurb:
      "Contacts, pipelines and deals — all wired to your inbox. Turn any email into a deal in one click, and let Novo AI qualify leads and draft outreach for you.",
    icon: Users,
    tint: "#ec4899",
    accent: "rgba(236,72,153,0.18)",
  },
  {
    id: "project",
    title: "Projects & Tasks Pro",
    blurb:
      "Colorful Kanban boards, comments with @mentions, attachments and a Novo AI chatbot that summarizes progress and answers status questions in seconds.",
    icon: FolderKanban,
    tint: "#f97316",
    accent: "rgba(249,115,22,0.18)",
  },
];

export default function MarketingLanding({ onLogin, onRegister }) {
  const { enterDemo } = useAuth();
  return (
    <div
      data-testid="marketing-landing"
      className="fixed inset-0 z-[1400] overflow-y-auto"
      style={{
        backgroundImage:
          "linear-gradient(rgba(4,6,16,0.6), rgba(4,6,16,0.6) 25%, rgba(4,6,16,0.6) 75%, rgba(4,6,16,0.6)), url(" +
          (process.env.PUBLIC_URL || "") +
          "/desktop-wallpaper.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "scroll",
      }}
    >
      {/* Corner telemetry — matches the LoginScreen aesthetic */}
      <div className="absolute top-4 left-5 mono text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/45">
        // node://hexa-01 · public-gateway
      </div>
      <div className="absolute top-4 right-5 mono text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/45 flex items-center gap-2">
        <span className="w-1.5 h-1.5 bg-[#8b5cf6] rounded-full animate-pulse" />
        public · online
      </div>

      {/* Top navigation */}
      <header className="relative z-[1] flex items-center justify-between px-6 sm:px-10 pt-6">
        <div className="flex items-center gap-3">
          <img
            src="/hexa-marketing.png"
            alt="HEXA"
            style={{
              height: 220,
              width: "auto",
            }}
          />
          <BetaChip tone="outline" className="mt-[-140px]" />
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="landing-try-demo"
            onClick={enterDemo}
            className="px-4 py-2 mono text-[10.5px] uppercase tracking-[0.28em] text-[#fbbf24] hover:text-white transition flex items-center gap-1.5"
            style={{
              border: "1px solid rgba(251,191,36,0.55)",
              background: "rgba(251,191,36,0.08)",
              borderRadius: 2,
            }}
          >
            <Play size={11} /> Try Demo
          </button>
          <button
            data-testid="landing-login"
            onClick={onLogin}
            className="px-4 py-2 mono text-[10.5px] uppercase tracking-[0.28em] text-white/85 hover:text-white transition"
            style={{
              border: "1px solid rgba(255,255,255,0.18)",
              borderRadius: 2,
            }}
          >
            Log In
          </button>
          <button
            data-testid="landing-register"
            onClick={onRegister}
            className="px-4 py-2 mono text-[10.5px] uppercase tracking-[0.28em] text-white flex items-center gap-1.5"
            style={{
              background:
                "linear-gradient(120deg, rgba(139,92,246,0.85), rgba(59,130,246,0.85))",
              boxShadow: "0 6px 16px rgba(139,92,246,0.35)",
              border: "1px solid rgba(167,139,250,0.45)",
              borderRadius: 2,
            }}
          >
            Register <ArrowRight size={12} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-[1] w-full px-6 sm:px-10 pt-8 sm:pt-10 pb-16 grid grid-cols-1 lg:grid-cols-[1.05fr,0.95fr] gap-12 items-center">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.4em] text-[#a78bfa] mb-5 flex items-center gap-2">
            <Sparkles size={11} /> the agentic web operating system
          </div>
          <h1
            className="text-white"
            style={{
              fontFamily: '"Montserrat", "Inter", system-ui, sans-serif',
              fontWeight: 700,
              fontSize: "clamp(36px, 5.6vw, 72px)",
              lineHeight: 1.05,
              letterSpacing: "-0.015em",
              textShadow:
                "0 0 28px rgba(167,139,250,0.35), 0 0 8px rgba(96,165,250,0.3)",
            }}
          >
            Your computer,{" "}
            <span
              style={{
                background:
                  "linear-gradient(120deg, #a78bfa, #60a5fa 50%, #34d399)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              with a mind
            </span>
            .
          </h1>
          <p className="mt-6 text-white/75 text-base sm:text-lg leading-relaxed max-w-[560px]">
            HEXA is an AI-native web operating system that runs your email,
            chat, tasks, and meetings under one identity —{" "}
            <span className="text-[#a78bfa] mono text-sm">
              you@hexawebos.com
            </span>
            . One workspace. One AI. Everything you do, multiplied.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <button
              data-testid="landing-join-now"
              onClick={onRegister}
              className="group inline-flex items-center gap-2 px-7 py-3.5 mono text-[12px] uppercase tracking-[0.28em] text-white transition hover:-translate-y-0.5"
              style={{
                background:
                  "linear-gradient(120deg, rgba(139,92,246,0.95), rgba(59,130,246,0.95))",
                boxShadow:
                  "0 18px 48px rgba(139,92,246,0.4), inset 0 1px 0 rgba(255,255,255,0.18)",
                border: "1px solid rgba(167,139,250,0.55)",
                borderRadius: 2,
                clipPath:
                  "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))",
              }}
            >
              Join Now
              <ArrowRight size={14} className="transition group-hover:translate-x-0.5" />
            </button>
            <button
              data-testid="landing-hero-demo"
              onClick={enterDemo}
              className="inline-flex items-center gap-2 px-5 py-3.5 mono text-[11px] uppercase tracking-[0.28em] text-[#fbbf24] transition hover:-translate-y-0.5"
              style={{
                border: "1px solid rgba(251,191,36,0.55)",
                background: "rgba(251,191,36,0.08)",
                borderRadius: 2,
              }}
            >
              <Play size={12} /> Try Demo — no signup
            </button>
            <button
              data-testid="landing-explore-login"
              onClick={onLogin}
              className="inline-flex items-center gap-2 px-5 py-3.5 mono text-[11px] uppercase tracking-[0.28em] text-white/80 hover:text-white transition"
              style={{
                border: "1px solid rgba(255,255,255,0.18)",
                borderRadius: 2,
              }}
            >
              I already have a HEXA ID
            </button>
          </div>

          {/* Trust chips */}
          <div className="mt-10 flex flex-wrap gap-3 text-[10px] mono uppercase tracking-[0.24em] text-white/55">
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/3">
              <ShieldCheck size={11} className="text-[#34d399]" /> end-to-end encrypted
            </span>
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/3">
              <Zap size={11} className="text-[#fbbf24]" /> agentic workflows
            </span>
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/3">
              <Hexagon size={11} className="text-[#a78bfa]" /> unified identity
            </span>
          </div>
        </div>

        {/* Hero visual — live OS demo cycling through 4 scenes */}
        <div className="relative hidden lg:block">
          <HeroDemo />
          <div
            className="absolute -bottom-3 left-1/2 -translate-x-1/2 mono text-[9px] uppercase tracking-[0.32em] text-[#a78bfa]/75 px-3 py-1 rounded-full"
            style={{ background: "rgba(12,18,36,0.85)", border: "1px solid rgba(167,139,250,0.28)" }}
          >
            ▶ live · 4-scene loop
          </div>
        </div>
      </section>

      {/* Feature cards */}
      <section
        data-testid="landing-features"
        className="relative z-[1] w-full px-6 sm:px-10 pb-20"
      >
        <div className="mono text-[10px] uppercase tracking-[0.4em] text-white/45 mb-6">
          // what you get
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.id}
                id={`landing-feature-${f.id}`}
                data-testid={`landing-feature-${f.id}`}
                className="p-5 flex flex-col gap-3 transition hover:-translate-y-1"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(12,18,36,0.78), rgba(8,12,24,0.6))",
                  border: `1px solid ${f.tint}30`,
                  boxShadow: `0 18px 48px rgba(0,0,0,0.4), 0 0 0 1px ${f.accent} inset`,
                  clipPath:
                    "polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))",
                }}
              >
                <span
                  className="grid place-items-center"
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 6,
                    background: `linear-gradient(160deg, ${f.tint}, ${f.tint}99)`,
                    boxShadow: `0 8px 18px ${f.tint}40, inset 0 1px 0 rgba(255,255,255,0.18)`,
                  }}
                >
                  <Icon size={20} color="#fff" strokeWidth={1.8} />
                </span>
                <div>
                  <div className="text-white text-[15px] font-medium mb-1">
                    {f.title}
                  </div>
                  <div className="text-white/65 text-[12.5px] leading-relaxed">
                    {f.blurb}
                  </div>
                </div>
                <div className="mt-auto mono text-[9px] uppercase tracking-[0.28em] text-white/40">
                  // module ready
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative z-[1] w-full px-6 sm:px-10 pb-24">
        <div
          className="p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
          style={{
            background:
              "linear-gradient(120deg, rgba(139,92,246,0.18), rgba(59,130,246,0.12))",
            border: "1px solid rgba(167,139,250,0.32)",
            boxShadow:
              "0 30px 80px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
            clipPath:
              "polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px))",
          }}
        >
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.4em] text-[#a78bfa] mb-2">
              // claim your hexa id
            </div>
            <h2
              className="text-white"
              style={{
                fontFamily: '"Montserrat", "Inter", system-ui, sans-serif',
                fontWeight: 700,
                fontSize: "clamp(22px, 3vw, 36px)",
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
              }}
            >
              One identity. One AI. <span className="text-[#a78bfa]">@hexawebos.com</span>
            </h2>
            <p className="text-white/65 text-sm mt-2 max-w-[540px]">
              Sign up in 30 seconds. No credit card. Free forever for individual workspaces.
            </p>
          </div>
          <button
            data-testid="landing-cta-join"
            onClick={onRegister}
            className="group inline-flex items-center gap-2 px-7 py-3.5 mono text-[12px] uppercase tracking-[0.28em] text-white transition hover:-translate-y-0.5"
            style={{
              background:
                "linear-gradient(120deg, rgba(139,92,246,0.95), rgba(59,130,246,0.95))",
              boxShadow:
                "0 18px 48px rgba(139,92,246,0.4), inset 0 1px 0 rgba(255,255,255,0.18)",
              border: "1px solid rgba(167,139,250,0.55)",
              borderRadius: 2,
              clipPath:
                "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))",
            }}
          >
            Join Now <ArrowRight size={14} className="transition group-hover:translate-x-0.5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-[1] border-t border-white/8 py-6 px-6 sm:px-10 mono text-[10px] uppercase tracking-[0.3em] text-white/35">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <span>© HEXA · ai web operating system</span>
          <span className="text-white/45">v3 · production</span>
        </div>
        <div
          className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center justify-center gap-x-8 gap-y-2"
          data-testid="marketing-footer-legal"
        >
          <a href="/privacy" data-testid="marketing-link-privacy"
             className="hover:text-white transition tracking-[0.3em]">privacy policy</a>
          <a href="/terms" data-testid="marketing-link-terms"
             className="hover:text-white transition tracking-[0.3em]">terms &amp; conditions</a>
          <a href="/faq" data-testid="marketing-link-faq"
             className="hover:text-white transition tracking-[0.3em]">faq</a>
          <a href="/blog" data-testid="marketing-link-blog"
             className="hover:text-white transition tracking-[0.3em]">blog</a>
        </div>
      </footer>

      {/* keyframes for floating cards */}
      <style>{`
        @keyframes float-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>

      {/* HEXA Buddy — floating AI assistant */}
      <HexaBuddy />
    </div>
  );
}
